import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileHeader from "../components/profile/ProfileHeader";
import SecuritySettings from "../components/profile/SecuritySettings";
import PreferencesSettings from "../components/profile/PreferencesSettings";
import ProfessionalInfo from "../components/profile/ProfessionalInfo";
import { useAuth } from "../context/AuthContext";

// Debug component to show component boundaries - removed

// Loading spinner component
const LoadingSpinner = ({ message }) => (
  <div className="py-6 flex justify-center">
    <div className="text-center">
      <svg className="animate-spin h-10 w-10 text-[#ff1b6d] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p className="mt-3 text-white">{message}</p>
    </div>
  </div>
);

// Auth error component
const AuthError = () => (
  <div className="py-6">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-[#1e1e1e] rounded-lg p-6 border border-red-500">
        <h2 className="text-xl font-bold text-white">Error de autenticación</h2>
        <p className="mt-2 text-gray-300">
          No se pudo cargar la información del usuario. Por favor, intenta iniciar sesión nuevamente.
        </p>
      </div>
    </div>
  </div>
);

// Error display component outside of Profile to avoid hook ordering issues
const ErrorDisplay = ({ error, location, user, userData, activeTab }) => {
  console.error("Error rendering Profile component:", error);
  console.error("Error stack:", error.stack);
  console.error("Current user state:", { user, userData, activeTab });
  console.error("Current route:", { 
    pathname: location.pathname,
    search: location.search,
    hash: location.hash
  });
  
  // For debugging, let's log any other known properties of the error
  console.error("Error details:", {
    message: error.message,
    name: error.name,
    code: error.code,
    lineNumber: error.lineNumber,
    fileName: error.fileName,
    columnNumber: error.columnNumber
  });
  
  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1e1e1e] rounded-lg p-6 border border-red-500">
          <h2 className="text-xl font-bold text-white">Error de renderizado</h2>
          <p className="mt-2 text-gray-300">
            {error.message || "Ha ocurrido un error inesperado al renderizar la página de perfil."}
          </p>
          <pre className="mt-4 bg-[#252525] p-3 rounded text-xs text-gray-300 overflow-auto">
            {error.stack}
          </pre>
        </div>
      </div>
    </div>
  );
};

function Profile() {
  // 1. Debug logging
  console.log("Profile component is being rendered", new Date().toISOString());
  
  // 2. All hooks first - keep them together at the top of the component
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [userData, setUserData] = useState(null);
  const [notification, setNotification] = useState({ 
    show: false, 
    message: "",
    type: 'success' 
  });

  // Function to show notification
  const showNotification = (message, type = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });

    // Hide notification after 3 seconds
    setTimeout(() => {
      setNotification({
        show: false,
        message: '',
        type: 'success'
      });
    }, 3000);
  };

  // Handler for updating user data
  const handleUpdateUserData = useCallback((updatedData, isInitialLoad = false) => {
    setUserData(prev => ({
      ...prev,
      ...updatedData
    }));
    
    // Only show notification if it's not the initial load
    if (!isInitialLoad) {
      showNotification("Información del perfil actualizada");
    }
  }, [showNotification]);

  // Navigation helper function
  const navigateToTab = useCallback((tab) => {
    console.log(`Navigating to tab: ${tab}`);
    setActiveTab(tab);
  }, []);

  // Render the current tab content - define with other callbacks
  const renderTabContent = useCallback(() => {
    console.log(`Rendering tab content for: ${activeTab}`);
    const commonProps = {
      userData,
      onUpdateUserData: handleUpdateUserData,
      showNotification
    };
    
    switch (activeTab) {
      case "profile":
        return (
          <ProfileHeader {...commonProps} />
        );
      case "security":
        return (
          <SecuritySettings 
            userData={userData}
            showNotification={showNotification}
          />
        );
      case "preferences":
        return (
          <PreferencesSettings {...commonProps} />
        );
      case "professional":
        return (
          <ProfessionalInfo {...commonProps} />
        );
      default:
        console.warn(`Unknown tab: ${activeTab}`);
        return null;
    }
  }, [activeTab, userData, handleUpdateUserData, showNotification]);
  
  // 4. Effects - group them together
  // Auth state debugging effect
  useEffect(() => {
    console.log("Auth state in Profile:", { user, isLoading, timestamp: new Date().toISOString() });
  }, [user, isLoading]);
  
  // Check for tab parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) {
      console.log("Tab parameter detected:", tab);
      setActiveTab(tab);
    }
  }, []); // Remove activeTab from dependencies to prevent potential infinite loops
  
  // Route debugging
  useEffect(() => {
    console.log("Profile - Route info:", { 
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
      key: location.key
    });
  }, [location]);

  // Log dimensions after render
  useEffect(() => {
    let isMounted = true;
    
    if (profileRef.current && isMounted) {
      const { offsetWidth, offsetHeight } = profileRef.current;
      const rect = profileRef.current.getBoundingClientRect();
      console.log("Profile component dimensions:", { 
        width: offsetWidth, 
        height: offsetHeight,
        rect: {
          top: rect.top,
          left: rect.left,
          bottom: rect.bottom,
          right: rect.right
        },
        timestamp: new Date().toISOString()
      });
      console.log("Profile component is visible in DOM");
    } else {
      console.error("Profile ref is null - component may not be properly mounted");
    }
    
    return () => {
      isMounted = false;
      console.log("Cleaning up profile ref effect");
    };
  }, []);

  // Set user data once user is available
  useEffect(() => {
    if (user) {
      console.log("Setting user data in Profile from auth state:", user);
      try {
        const defaultData = {
          name: user.name || "Usuario Wemax",
          email: user.email || "usuario@wemax.com",
          bio: "Desarrollador de software con experiencia en React y diseño de interfaces de usuario.",
          profileImage: user.photoURL || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
          role: "Desarrollador Frontend",
          company: "WeMax",
          skills: ["React", "JavaScript", "TailwindCSS", "UI/UX"],
          location: "Madrid, España",
          socialLinks: {
            website: "https://miwebsite.com",
            github: "https://github.com/username",
            linkedin: "https://linkedin.com/in/username",
            twitter: "https://twitter.com/username"
          },
          preferences: {
            theme: 'dark',
            notifications: {
              email: true,
              push: true,
              updates: false
            },
            language: 'es',
            dateFormat: 'DD/MM/YYYY'
          }
        };

        setUserData(defaultData);
      } catch (error) {
        console.error("Error setting userData:", error);
        console.error("User object that caused error:", JSON.stringify(user, null, 2));
      }
    }
  }, [user]);

  // 5. Loading states - after all hooks but before try-catch
  if (isLoading) {
    console.log("Profile: Loading state detected");
    return <LoadingSpinner message="Cargando información del perfil..." />;
  }

  if (!user) {
    console.log("Profile: No user detected");
    return <AuthError />;
  }

  if (!userData) {
    console.log("Profile: No userData detected");
    return <LoadingSpinner message="Preparando perfil de usuario..." />;
  }

  // 6. Main render with error handling
  try {
    console.log("Profile: Attempting to render main content");
    return (
      <div ref={profileRef} className="py-6 relative z-10">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#1e1e1e] rounded-lg p-6 min-h-[200px]">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white">Mi Perfil</h1>
            <p className="mt-1 text-gray-400">
              Gestiona tu información personal y configuraciones
            </p>
          </div>

          {/* Notification */}
          {notification.show && (
            <div className={`fixed top-4 right-4 bg-[#1e1e1e] border-l-4 ${notification.type === 'success' ? 'border-green-500' : 'border-[#ff1b6d]'} text-white px-6 py-3 rounded shadow-lg z-50 transition-all`}>
              <div className="flex items-center">
                <svg className={`h-5 w-5 ${notification.type === 'success' ? 'text-green-500' : 'text-[#ff1b6d]'} mr-3`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{notification.message}</span>
              </div>
            </div>
          )}

          {/* Navigation Tabs */}
          <div className="mb-6 border-b border-[#2a2a2a]">
            <nav className="flex space-x-8">
              <button
                onClick={() => navigateToTab("profile")}
                className={`py-3 px-1 text-sm font-medium border-b-2 ${
                  activeTab === "profile"
                    ? "border-[#ff1b6d] text-[#ff1b6d]"
                    : "border-transparent text-gray-400 hover:text-white"
                }`}
              >
                Perfil
              </button>
              <button
                onClick={() => navigateToTab("security")}
                className={`py-3 px-1 text-sm font-medium border-b-2 ${
                  activeTab === "security"
                    ? "border-[#ff1b6d] text-[#ff1b6d]"
                    : "border-transparent text-gray-400 hover:text-white"
                }`}
              >
                Seguridad
              </button>
              <button
                onClick={() => navigateToTab("preferences")}
                className={`py-3 px-1 text-sm font-medium border-b-2 ${
                  activeTab === "preferences"
                    ? "border-[#ff1b6d] text-[#ff1b6d]"
                    : "border-transparent text-gray-400 hover:text-white"
                }`}
              >
                Preferencias
              </button>
              <button
                onClick={() => navigateToTab("professional")}
                className={`py-3 px-1 text-sm font-medium border-b-2 ${
                  activeTab === "professional"
                    ? "border-[#ff1b6d] text-[#ff1b6d]"
                    : "border-transparent text-gray-400 hover:text-white"
                }`}
              >
                Información Profesional
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-[#1e1e1e] rounded-lg">
            {renderTabContent()}
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error("Profile: Error caught in render:", error);
    return (
      <ErrorDisplay 
        error={error}
        location={location}
        user={user}
        userData={userData}
        activeTab={activeTab}
      />
    );
  }
}

export default Profile;
